import { FormEvent, useEffect, useState } from "react";
import LoginForm from "../components/LoginForm";
import {
  AdminUser,
  adminCreateUser,
  adminListUsers,
  adminRevokeUser,
  adminTgAddSubject,
  adminTgGetBotModels,
  adminTgListSubjects,
  adminTgRemoveSubject,
  adminTgSetBotModels,
  ApiError,
  TelegramBotModels,
  TelegramSubject,
  TelegramSubjectKind,
} from "../lib/api";
import { clearAdminSecret, getAdminSecret, setAdminSecret } from "../lib/storage";

export default function AdminPage() {
  const [secret, setSecret] = useState<string | null>(() => getAdminSecret());
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!secret) {
      setChecking(false);
      return;
    }
    adminListUsers(secret)
      .then(() => setChecking(false))
      .catch(() => {
        clearAdminSecret();
        setSecret(null);
        setChecking(false);
      });
  }, [secret]);

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center text-slate-400">加载中…</div>;
  }

  if (!secret) {
    return (
      <LoginForm
        title="密钥管理后台"
        description="仅限管理员访问"
        fieldLabel="Admin Secret"
        placeholder="管理员密钥"
        submitLabel="进入"
        onSubmit={async (value) => {
          await adminListUsers(value); // 验证有效性
          setAdminSecret(value);
          setSecret(value);
        }}
      />
    );
  }

  return (
    <AdminPanel
      secret={secret}
      onLogout={() => {
        clearAdminSecret();
        setSecret(null);
      }}
    />
  );
}

type Tab = "apikeys" | "telegram";

function AdminPanel({ secret, onLogout }: { secret: string; onLogout: () => void }) {
  const [tab, setTab] = useState<Tab>("apikeys");

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">管理后台</h1>
        <button
          onClick={onLogout}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          退出
        </button>
      </div>

      <div className="mt-4 flex gap-1 rounded-lg bg-slate-100 p-1">
        {(
          [
            ["apikeys", "团队 API Key"],
            ["telegram", "Telegram Bot"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
              tab === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "apikeys" ? (
        <ApiKeyPanel secret={secret} onLogout={onLogout} />
      ) : (
        <TelegramPanel secret={secret} onLogout={onLogout} />
      )}
    </div>
  );
}

function useAdminError(onLogout: () => void) {
  const [error, setError] = useState<string | null>(null);
  function handleError(err: unknown) {
    if (err instanceof ApiError && err.status === 401) {
      onLogout();
      return;
    }
    setError(err instanceof Error ? err.message : "操作失败");
  }
  return { error, setError, handleError };
}

// ---------- 团队 API Key ----------

function ApiKeyPanel({ secret, onLogout }: { secret: string; onLogout: () => void }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const { error, setError, handleError } = useAdminError(onLogout);
  const [newKey, setNewKey] = useState<{ userId: string; apiKey: string } | null>(null);

  async function reload() {
    setLoading(true);
    try {
      setUsers(await adminListUsers(secret));
      setError(null);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // React 会在事件处理函数里出现 await 之后把 e.currentTarget 置为 null，
    // 必须在任何 await 之前把表单元素引用取出来，后面复用这个引用而不是再读 e.currentTarget
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    const userId = String(form.get("user_id") || "").trim();
    const name = String(form.get("name") || "").trim();
    if (!userId) return;

    try {
      const result = await adminCreateUser(secret, userId, name);
      setNewKey({ userId: result.user_id, apiKey: result.api_key });
      setError(null);
      formEl.reset();
      await reload();
    } catch (err) {
      handleError(err);
    }
  }

  async function handleRevoke(userId: string) {
    if (!confirm(`确认吊销 ${userId} 的 API Key？此操作不可撤销。`)) return;
    try {
      await adminRevokeUser(secret, userId);
      await reload();
    } catch (err) {
      handleError(err);
    }
  }

  return (
    <div>
      {newKey && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">
            {newKey.userId} 的 Key 已生成，仅此一次显示，请立即安全发送给本人：
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg bg-white px-3 py-2 text-xs">
              {newKey.apiKey}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(newKey.apiKey)}
              className="shrink-0 rounded-lg bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-700"
            >
              复制
            </button>
          </div>
          <button
            onClick={() => setNewKey(null)}
            className="mt-2 text-xs text-amber-700 underline"
          >
            我已保存，关闭提示
          </button>
        </div>
      )}

      <form
        onSubmit={handleCreate}
        className="mt-6 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4"
      >
        <div>
          <label className="block text-xs font-medium text-slate-600">user_id</label>
          <input
            name="user_id"
            required
            pattern="[A-Za-z0-9_\-]{1,64}"
            title="仅允许字母、数字、下划线、连字符"
            placeholder="zhangsan"
            className="mt-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">展示名</label>
          <input
            name="name"
            placeholder="张三"
            className="mt-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500"
          />
        </div>
        <button
          type="submit"
          className="rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
        >
          生成 Key
        </button>
      </form>

      {error && <p className="mt-3 text-sm text-red-600">⚠️ {error}</p>}

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">user_id</th>
              <th className="px-4 py-2 font-medium">展示名</th>
              <th className="px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="px-4 py-4 text-center text-slate-400">
                  加载中…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-4 text-center text-slate-400">
                  暂无成员
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.user_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{u.user_id}</td>
                  <td className="px-4 py-2">{u.name}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleRevoke(u.user_id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      吊销
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Telegram Bot ----------

function TelegramPanel({ secret, onLogout }: { secret: string; onLogout: () => void }) {
  const { error, setError, handleError } = useAdminError(onLogout);

  return (
    <div>
      {error && <p className="mt-3 text-sm text-red-600">⚠️ {error}</p>}
      <TelegramBotModelsCard secret={secret} onError={handleError} clearError={() => setError(null)} />
      <TelegramSubjectCard
        secret={secret}
        kind="users"
        title="授权用户（私聊）"
        idLabel="用户 ID"
        idPlaceholder="123456789"
        hint="用户对 Bot 发送 /id 可查看自己的用户 ID"
        onError={handleError}
        clearError={() => setError(null)}
      />
      <TelegramSubjectCard
        secret={secret}
        kind="groups"
        title="授权群组"
        idLabel="群组 ID"
        idPlaceholder="-1001234567890"
        hint="把 Bot 拉进群后，在群里发送 /id 可查看群组 ID（负数）"
        onError={handleError}
        clearError={() => setError(null)}
      />
    </div>
  );
}

function TelegramBotModelsCard({
  secret,
  onError,
  clearError,
}: {
  secret: string;
  onError: (err: unknown) => void;
  clearError: () => void;
}) {
  const [models, setModels] = useState<TelegramBotModels | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedTip, setSavedTip] = useState(false);

  useEffect(() => {
    adminTgGetBotModels(secret).then(setModels).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleChange(role: "diagnostic_model" | "classifier_model", value: string) {
    setSaving(true);
    try {
      // value === "" 表示恢复目录默认
      setModels(await adminTgSetBotModels(secret, { [role]: value }));
      clearError();
      setSavedTip(true);
      setTimeout(() => setSavedTip(false), 2000);
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  }

  if (!models) {
    return (
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400">
        加载中…
      </div>
    );
  }

  const roles: ["diagnostic_model" | "classifier_model", string, string | null, string][] = [
    ["diagnostic_model", "诊断 / 问答模型", models.diagnostic_model, models.default_diagnostic_model],
    ["classifier_model", "意图分类模型", models.classifier_model, models.default_classifier_model],
  ];

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Bot 模型配置</h2>
        {savedTip && <span className="text-xs text-emerald-600">✓ 已保存，下一条消息生效</span>}
      </div>
      <p className="mt-1 text-xs text-slate-500">作用于所有 Telegram 会话（私聊与群组）</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {roles.map(([role, label, current, fallback]) => (
          <div key={role}>
            <label className="block text-xs font-medium text-slate-600">{label}</label>
            <select
              value={current ?? ""}
              disabled={saving}
              onChange={(e) => handleChange(role, e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500 disabled:opacity-50"
            >
              <option value="">默认（{fallback}）</option>
              {models.available_models.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.display_name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

function TelegramSubjectCard({
  secret,
  kind,
  title,
  idLabel,
  idPlaceholder,
  hint,
  onError,
  clearError,
}: {
  secret: string;
  kind: TelegramSubjectKind;
  title: string;
  idLabel: string;
  idPlaceholder: string;
  hint: string;
  onError: (err: unknown) => void;
  clearError: () => void;
}) {
  const [subjects, setSubjects] = useState<TelegramSubject[]>([]);
  const [loading, setLoading] = useState(true);

  async function reload() {
    setLoading(true);
    try {
      setSubjects(await adminTgListSubjects(secret, kind));
      clearError();
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    const tgId = Number(String(form.get("tg_id") || "").trim());
    const note = String(form.get("note") || "").trim();
    if (!Number.isInteger(tgId) || tgId === 0) return;

    try {
      await adminTgAddSubject(secret, kind, tgId, note);
      formEl.reset();
      await reload();
    } catch (err) {
      onError(err);
    }
  }

  async function handleRemove(tgId: number) {
    if (!confirm(`确认移除授权 ${tgId}？移除后将立即无法使用 Bot。`)) return;
    try {
      await adminTgRemoveSubject(secret, kind, tgId);
      await reload();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>

      <form onSubmit={handleAdd} className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600">{idLabel}</label>
          <input
            name="tg_id"
            required
            pattern="-?[0-9]{1,20}"
            title="Telegram 数字 ID"
            placeholder={idPlaceholder}
            className="mt-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">备注</label>
          <input
            name="note"
            placeholder={kind === "users" ? "张三" : "运维告警群"}
            className="mt-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500"
          />
        </div>
        <button
          type="submit"
          className="rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
        >
          添加授权
        </button>
      </form>

      <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">{idLabel}</th>
              <th className="px-4 py-2 font-medium">备注</th>
              <th className="px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="px-4 py-4 text-center text-slate-400">
                  加载中…
                </td>
              </tr>
            ) : subjects.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-4 text-center text-slate-400">
                  暂无授权
                </td>
              </tr>
            ) : (
              subjects.map((s) => (
                <tr key={s.tg_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{s.tg_id}</td>
                  <td className="px-4 py-2">{s.note}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleRemove(s.tg_id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      移除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
